'use client'

import { useCallback, useEffect, useRef, useState, type AnimationEvent } from 'react'
import { usePathname } from 'next/navigation'
import Link from 'next/link'
import { IconArrowRight, IconArrowLeft } from '@/app/components/atoms/Icons'
import { useAppPromotionCookie } from '@/app/lib/hooks/useAppPromotionCookie'
import { cx } from '@/app/lib/utils'
import { route } from '@/app/lib/routing'
import { NAV_HOME_LINK, NAV_LINKS } from './constants'
import { isNavHrefCurrent } from './isNavHrefCurrent'
import { useNavClickTracking } from './useNavClickTracking'
import type { NavCategory, NavLink } from './types'
import styles from './NavDrawer.module.scss'

interface NavDrawerProps {
  open: boolean
  onClose: () => void
  /** Categorías (mapeadas del `menu` del layout con `toNavCategories`). */
  categories: NavCategory[]
  /** Enlaces de sección. Por defecto, los estáticos de la app. */
  links?: NavLink[]
}

/**
 * El hueco superior (para que el header, que va por encima en z-index, no tape
 * la primera fila del menú) lo pone el CSS con `--header-bottom`, la variable
 * que el propio `Header` publica y mantiene al día con un `ResizeObserver`.
 * Antes llegaba como prop `headerBottom` medida en un estado de React que
 * arrancaba en 0 y, si el menú se pintaba antes de la primera medición, se
 * quedaba sin desplazamiento y el header tapaba la primera categoría.
 */
export function NavDrawer({ open, onClose, categories, links = NAV_LINKS }: NavDrawerProps) {
  const pathname = usePathname()
  const [activeItem, setActiveItem] = useState<NavCategory | null>(null)
  // El panel de subcategorías sigue montado mientras corre su animación de
  // salida; se desmonta en `animationend`.
  const [isClosingSub, setIsClosingSub] = useState(false)
  const drawerRef = useRef<HTMLDivElement>(null)
  const megaMenuRef = useRef<HTMLDivElement>(null)
  const subBackRef = useRef<HTMLButtonElement>(null)
  /** Categoría cuyo panel de subcategorías se abrió: al volver, el foco
   * regresa a su botón. */
  const lastCategoryIdRef = useRef<string | null>(null)
  // "Consulta tus entradas" no aplica dentro de la propia app (cookie
  // `eciAppVersion=eci-app-focus`) — la app ya tiene su propia sección de
  // entradas compradas.
  const isAppContext = useAppPromotionCookie()
  const visibleLinks = isAppContext ? links.filter((item) => item.id !== 'postsale') : links

  // El backend puede devolver como categoría una sección que ya existe como
  // enlace fijo (p. ej. "Eventos online"): en móvil se conserva en la lista de
  // categorías y se omite de "Más secciones" para que no aparezca duplicada.
  const categoryIds = new Set(categories.map((item) => item.id))
  const mobileLinks = visibleLinks.filter((item) => !categoryIds.has(item.href.split('/')[1] ?? ''))

  // Cierra el drawer reseteando también el panel de subcategorías.
  const handleClose = useCallback(() => {
    setActiveItem(null)
    setIsClosingSub(false)
    onClose()
  }, [onClose])

  // Con `prefers-reduced-motion: reduce` el CSS anula la animación y
  // `animationend` no llegaría nunca, así que se desmonta al momento (también
  // cuando no hay `matchMedia`, p. ej. en jsdom).
  const handleBack = useCallback(() => {
    const animates = window.matchMedia?.('(prefers-reduced-motion: no-preference)').matches ?? false
    if (animates) {
      setIsClosingSub(true)
    } else {
      setActiveItem(null)
    }
  }, [])

  const handleSubAnimationEnd = useCallback(
    (e: AnimationEvent<HTMLDivElement>) => {
      if (e.target !== e.currentTarget || !isClosingSub) return
      setActiveItem(null)
      setIsClosingSub(false)
    },
    [isClosingSub],
  )

  useEffect(() => {
    if (!open) return
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (activeItem) handleBack()
        else handleClose()
      }
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [open, handleClose, handleBack, activeItem])

  // Foco (WCAG 2.4.3). El botón que abre y cierra (la «X») vive en el header,
  // fuera del panel, así que el panel no es modal: al abrir, el foco entra en
  // el primer enlace del panel visible (móvil o mega menú, el otro está oculto
  // por CSS) para no obligar a recorrer antes el resto del header. La vuelta
  // del foco a la «X» al cerrar la hace `Header`.
  useEffect(() => {
    if (!open) return
    const panel = [drawerRef.current, megaMenuRef.current].find((el) => el?.getClientRects().length)
    panel?.querySelector<HTMLElement>('a[href], button')?.focus({ preventScroll: true })
  }, [open])

  // Subcategorías (móvil): al entrar, el foco va a «Volver»; al volver, al
  // botón de la categoría que las abrió (el nivel 2 tapa al nivel 1).
  useEffect(() => {
    if (activeItem) {
      lastCategoryIdRef.current = activeItem.id
      subBackRef.current?.focus({ preventScroll: true })
      return
    }
    const id = lastCategoryIdRef.current
    if (!id) return
    lastCategoryIdRef.current = null
    drawerRef.current
      ?.querySelector<HTMLElement>(`[data-nav-category="${CSS.escape(id)}"]`)
      ?.focus({ preventScroll: true })
  }, [activeItem])

  // `click` de analítica + cierre del drawer. Un único punto para móvil y
  // escritorio (un solo push por clic; el botón de categoría con subcategorías
  // no navega y por eso no pasa por aquí).
  const trackNavClick = useNavClickTracking()
  const handleNavClick = useCallback(
    (label: string, href: string, hasLocation = true) => {
      trackNavClick({ label, href, hasLocation })
      handleClose()
    },
    [trackNavClick, handleClose],
  )

  const handleCategoryClick = useCallback(
    (item: NavCategory) => {
      if (item.subcategories?.length) {
        setIsClosingSub(false)
        setActiveItem(item)
      } else handleClose()
    },
    [handleClose],
  )

  if (!open) return null

  const homeHref = route(NAV_HOME_LINK.href)
  const isHomeActive = pathname === homeHref

  // Todos los enlaces con `prefetch={false}`: al abrir el menú en escritorio
  // entran a la vez en el viewport los ~100 del mega menú, y el prefetch
  // automático de `next/link` lanzaba cientos de peticiones RSC de golpe.

  return (
    <>
      <div className={styles.scrim} onClick={handleClose} aria-hidden="true" />

      {/* MÓVIL — drawer nivel 1: panel lateral (max-width 350px) */}
      <div
        ref={drawerRef}
        className={styles.drawer}
        role="dialog"
        aria-label="Menú de navegación"
      >
        <nav className={styles.mobileNav} aria-label="Categorías">
          <ul className={styles.mobileList}>
            <li>
              <Link
                href={homeHref}
                prefetch={false}
                className={cx(styles.mobileItem, isHomeActive && styles.mobileItemActive)}
                aria-current={isHomeActive ? 'page' : undefined}
                onClick={() => handleNavClick(NAV_HOME_LINK.label, NAV_HOME_LINK.href, false)}
              >
                <span className={styles.mobileItemLabel}>{NAV_HOME_LINK.label.toUpperCase()}</span>
                <IconArrowRight size={16} className={styles.mobileArrow} />
              </Link>
            </li>
            {categories.map((item, index) => {
              const hasSub = Boolean(item.subcategories?.length)
              const isActive = isNavHrefCurrent(pathname, item.href)
              const className = cx(styles.mobileItem, isActive && styles.mobileItemActive)
              return (
                <li key={item.id + '-' + index}>
                  {hasSub ? (
                    <button
                      className={className}
                      data-nav-category={item.id}
                      onClick={() => handleCategoryClick(item)}
                      aria-expanded={activeItem?.id === item.id}
                      aria-current={isActive ? 'page' : undefined}
                      aria-haspopup="dialog"
                    >
                      <span className={styles.mobileItemLabel}>{item.label.toUpperCase()}</span>
                      <IconArrowRight size={16} className={styles.mobileArrow} />
                    </button>
                  ) : (
                    <Link
                      href={route(item.href)}
                      prefetch={false}
                      className={className}
                      aria-current={isActive ? 'page' : undefined}
                      onClick={() => handleNavClick(item.label, item.href)}
                    >
                      <span className={styles.mobileItemLabel}>{item.label.toUpperCase()}</span>
                      <IconArrowRight size={16} className={styles.mobileArrow} />
                    </Link>
                  )}
                </li>
              )
            })}
          </ul>
          <ul
            className={cx(styles.mobileList, styles.mobileListSecondary)}
            aria-label="Más secciones"
          >
            {mobileLinks.map((item) => {
              const isActive = pathname.startsWith(route(item.href))
              return (
                <li key={item.id}>
                  <Link
                    href={route(item.href)}
                    prefetch={false}
                    className={cx(
                      styles.mobileItem,
                      styles.mobileItemSub,
                      isActive && styles.mobileItemActive,
                    )}
                    onClick={() => handleNavClick(item.label, item.href, false)}
                  >
                    <span className={styles.mobileItemLabel}>{item.label}</span>
                    <IconArrowRight size={16} className={styles.mobileArrow} />
                  </Link>
                </li>
              )
            })}
          </ul>
        </nav>
      </div>

      {/* MÓVIL — nivel 2: subcategorías */}
      {activeItem && (
        <div
          className={cx(styles.subDrawer, isClosingSub && styles.subDrawerClosing)}
          onAnimationEnd={handleSubAnimationEnd}
          role="dialog"
          aria-label={`Subcategorías de ${activeItem.label}`}
        >
          <div className={styles.subCategoryRow}>
            <button
              ref={subBackRef}
              onClick={handleBack}
              aria-label="Volver"
              className={styles.subCategoryBack}
            >
              <IconArrowLeft size={16} />
            </button>
            <span className={styles.subCategoryTitle}>{activeItem.label.toUpperCase()}</span>
            <span className={styles.subCategoryBackSpacer} aria-hidden="true" />
          </div>

          <nav className={styles.mobileNav} aria-label={`Subcategorías de ${activeItem.label}`}>
            <ul className={styles.mobileList}>
              <li>
                <Link
                  href={route(activeItem.href)}
                  prefetch={false}
                  className={cx(
                    styles.mobileItem,
                    styles.mobileItemSub,
                    isNavHrefCurrent(pathname, activeItem.href) && styles.mobileItemActive,
                  )}
                  onClick={() => handleNavClick(`Todo en ${activeItem.label}`, activeItem.href)}
                >
                  <span className={styles.mobileItemLabel}>Todo en {activeItem.label}</span>
                  <IconArrowRight size={16} className={styles.mobileArrow} />
                </Link>
              </li>
              {activeItem.subcategories?.map((sub, subIndex) => (
                <li key={sub.href + '-' + subIndex}>
                  <Link
                    href={route(sub.href)}
                    prefetch={false}
                    className={cx(
                      styles.mobileItem,
                      styles.mobileItemSub,
                      isNavHrefCurrent(pathname, sub.href) && styles.mobileItemActive,
                    )}
                    onClick={() => handleNavClick(sub.label, sub.href)}
                  >
                    <span className={styles.mobileItemLabel}>{sub.label}</span>
                    <IconArrowRight size={16} className={styles.mobileArrow} />
                  </Link>
                </li>
              ))}
            </ul>
          </nav>
        </div>
      )}

      {/* DESKTOP — mega menú (oculto en mobile vía CSS) */}
      <div
        ref={megaMenuRef}
        className={styles.megaMenu}
        role="dialog"
        aria-label="Menú de navegación"
      >
        <nav className={styles.megaNav} aria-label="Todas las categorías">
          <div className={styles.megaGrid}>
            {/* Col 1: Home + Conciertos, en un bloque que no se parte: por
                debajo de 1440px el mega menú reparte los grupos en columnas
                CSS y, sueltos, Conciertos saltaba a la columna siguiente. */}
            <div className={styles.megaCol}>
              <div className={styles.megaLead}>
                <div className={styles.megaGroup}>
                  <Link
                    href={homeHref}
                    prefetch={false}
                    className={cx(styles.megaCategoryHeader, isHomeActive && styles.megaActive)}
                    aria-current={isHomeActive ? 'page' : undefined}
                    onClick={() => handleNavClick(NAV_HOME_LINK.label, NAV_HOME_LINK.href, false)}
                  >
                    <span>{NAV_HOME_LINK.label.toUpperCase()}</span>
                    <IconArrowRight size={14} className={styles.megaArrow} />
                  </Link>
                </div>
                {[categories[0]].filter(Boolean).map((cat) => (
                  <div key={cat.id} className={styles.megaGroup}>
                    <Link
                      href={route(cat.href)}
                      prefetch={false}
                      className={cx(
                        styles.megaCategoryHeader,
                        isNavHrefCurrent(pathname, cat.href) && styles.megaActive,
                      )}
                      onClick={() => handleNavClick(cat.label, cat.href)}
                    >
                      <span>{cat.label.toUpperCase()}</span>
                      <IconArrowRight size={14} className={styles.megaArrow} />
                    </Link>
                    {cat.subcategories?.map((sub, subIndex) => (
                      <Link
                        key={sub.href + '-' + subIndex}
                        href={route(sub.href)}
                        prefetch={false}
                        className={cx(
                          styles.megaSubItem,
                          isNavHrefCurrent(pathname, sub.href) && styles.megaActive,
                        )}
                        onClick={() => handleNavClick(sub.label, sub.href)}
                      >
                        <span>{sub.label}</span>
                        <IconArrowRight size={13} className={styles.megaArrow} />
                      </Link>
                    ))}
                  </div>
                ))}
              </div>
            </div>

            {/* Col 2: Teatro + Musicales */}
            <div className={styles.megaCol}>
              {[categories[1], categories[2]].filter(Boolean).map((cat) => (
                <div key={cat.id} className={styles.megaGroup}>
                  <Link
                    href={route(cat.href)}
                    prefetch={false}
                    className={cx(
                      styles.megaCategoryHeader,
                      isNavHrefCurrent(pathname, cat.href) && styles.megaActive,
                    )}
                    onClick={() => handleNavClick(cat.label, cat.href)}
                  >
                    <span>{cat.label.toUpperCase()}</span>
                    <IconArrowRight size={14} className={styles.megaArrow} />
                  </Link>
                  {cat.subcategories?.map((sub) => (
                    <Link
                      key={sub.href}
                      href={route(sub.href)}
                      prefetch={false}
                      className={cx(
                        styles.megaSubItem,
                        isNavHrefCurrent(pathname, sub.href) && styles.megaActive,
                      )}
                      onClick={() => handleNavClick(sub.label, sub.href)}
                    >
                      <span>{sub.label}</span>
                      <IconArrowRight size={13} className={styles.megaArrow} />
                    </Link>
                  ))}
                </div>
              ))}
            </div>

            {/* Col 3: Festivales + Deporte */}
            <div className={styles.megaCol}>
              {[categories[3], categories[4]].filter(Boolean).map((cat) => (
                <div key={cat.id} className={styles.megaGroup}>
                  <Link
                    href={route(cat.href)}
                    prefetch={false}
                    className={cx(
                      styles.megaCategoryHeader,
                      isNavHrefCurrent(pathname, cat.href) && styles.megaActive,
                    )}
                    onClick={() => handleNavClick(cat.label, cat.href)}
                  >
                    <span>{cat.label.toUpperCase()}</span>
                    <IconArrowRight size={14} className={styles.megaArrow} />
                  </Link>
                  {cat.subcategories?.map((sub) => (
                    <Link
                      key={sub.href}
                      href={route(sub.href)}
                      prefetch={false}
                      className={cx(
                        styles.megaSubItem,
                        isNavHrefCurrent(pathname, sub.href) && styles.megaActive,
                      )}
                      onClick={() => handleNavClick(sub.label, sub.href)}
                    >
                      <span>{sub.label}</span>
                      <IconArrowRight size={13} className={styles.megaArrow} />
                    </Link>
                  ))}
                </div>
              ))}
            </div>

            {/* Col 4: Cultura + Experiencias */}
            <div className={styles.megaCol}>
              {[categories[5], categories[6]].filter(Boolean).map((cat) => (
                <div key={cat.id} className={styles.megaGroup}>
                  <Link
                    href={route(cat.href)}
                    prefetch={false}
                    className={cx(
                      styles.megaCategoryHeader,
                      isNavHrefCurrent(pathname, cat.href) && styles.megaActive,
                    )}
                    onClick={() => handleNavClick(cat.label, cat.href)}
                  >
                    <span>{cat.label.toUpperCase()}</span>
                    <IconArrowRight size={14} className={styles.megaArrow} />
                  </Link>
                  {cat.subcategories?.map((sub) => (
                    <Link
                      key={sub.href}
                      href={route(sub.href)}
                      prefetch={false}
                      className={cx(
                        styles.megaSubItem,
                        isNavHrefCurrent(pathname, sub.href) && styles.megaActive,
                      )}
                      onClick={() => handleNavClick(sub.label, sub.href)}
                    >
                      <span>{sub.label}</span>
                      <IconArrowRight size={13} className={styles.megaArrow} />
                    </Link>
                  ))}
                </div>
              ))}
            </div>

            {/* Col 5: En familia + enlaces secundarios */}
            <div className={styles.megaCol}>
              {[categories[7]].filter(Boolean).map((cat) => (
                <div key={cat.id} className={styles.megaGroup}>
                  <Link
                    href={route(cat.href)}
                    prefetch={false}
                    className={cx(
                      styles.megaCategoryHeader,
                      isNavHrefCurrent(pathname, cat.href) && styles.megaActive,
                    )}
                    onClick={() => handleNavClick(cat.label, cat.href)}
                  >
                    <span>{cat.label.toUpperCase()}</span>
                    <IconArrowRight size={14} className={styles.megaArrow} />
                  </Link>
                  {cat.subcategories?.map((sub) => (
                    <Link
                      key={sub.href}
                      href={route(sub.href)}
                      prefetch={false}
                      className={cx(
                        styles.megaSubItem,
                        isNavHrefCurrent(pathname, sub.href) && styles.megaActive,
                      )}
                      onClick={() => handleNavClick(sub.label, sub.href)}
                    >
                      <span>{sub.label}</span>
                      <IconArrowRight size={13} className={styles.megaArrow} />
                    </Link>
                  ))}
                </div>
              ))}
              <div className={styles.megaGroup}>
                {visibleLinks.map((item) => (
                  <Link
                    key={item.id}
                    href={route(item.href)}
                    prefetch={false}
                    className={styles.megaCategoryHeader}
                    onClick={() => handleNavClick(item.label, item.href, false)}
                  >
                    <span>{item.label.toUpperCase()}</span>
                    <IconArrowRight size={14} className={styles.megaArrow} />
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </nav>
      </div>
    </>
  )
}
